import { Component, DOCUMENT, effect, inject, Input, OnInit, Optional, signal, ViewChild } from '@angular/core';
import { Route, ActivatedRoute, Router } from '@angular/router';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { SyntheseVolumesService } from 'src/app/data/declaration/services/synthese-volumes/synthese-volumes.service';
import { SyntheseVolumesAOC } from 'src/app/data/declaration/models/synthese-volumes-aoc.model';
import { ChangementsDenominationComponent } from './modals/changements-denomination/changements-denomination.component';
import { SaisirConditionnementComponent } from './modals/saisir-conditionnement/saisir-conditionnement.component';
import { SessionContext, NavigationContext } from 'src/app/core/services/config/app.settings';
import { SyntheseVolumesIGP } from 'src/app/data/declaration/models/synthese-volumes-igp.model';
import { forkJoin } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { RechercheProduitComponent } from './modals/recherche-produit/recherche-produit.component';
import { TypeCahierCode } from 'src/app/data/habilitation/models/enums/type-cahier-code.enum';
import { Produit } from 'src/app/data/habilitation/models/produit.model';
import { Fail } from 'src/app/shared/errors/fail.error';
import { StockCode } from 'src/app/data/declaration/models/enums/stock.enum';
import { ProduitsService } from 'src/app/data/declaration/services/produits/produits.service';
import { SaisirTransactionComponent } from './modals/saisir-transaction/saisir-transaction.component';
import { SaisirConditionnementIgpComponent } from './modals/saisir-conditionnement-igp/saisir-conditionnement-igp.component';
import { SaisieRevendicationResult } from 'src/app/data/declaration/interfaces/saisie-revendication-result.interface';
import { LotSaisiesRevendications } from 'src/app/data/declaration/models/lot-saisies-revendications.model';
import { RefCampagnesService } from 'src/app/data/declaration/services/ref-campagnes/ref-campagnes.service';
import { DonneesDrComponent } from './modals/donnees-dr/donnees-dr.component';
import { UtilisateurTypeCode } from '../../../../../data/intervenant/models/enums/type-utilisateur.enum';
import { SaisirTirageComponent } from './modals/saisir-tirage/saisir-tirage.component';
import { CahiersService } from 'src/app/data/commission/services/cahiers/cahiers.service';
import { DecimalPipe, NgClass } from '@angular/common';
import { LoaderService } from '../../../../../core/services/loader/loader.service';
import { Column } from '../../../../../shared/models/column.model';
import { SaisirRevendicationComponent } from './modals/saisir-revendication/saisir-revendication.component';
import { SyntheseSaisieRevendicationComponent } from './modals/saisir-revendication/synthese-saisie-revendication/synthese-saisie-revendication.component';
import { ColumnVisibilityModalComponent } from '../../../../../shared/components/modals/column-visibility-modal/column-visibility-modal.component';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { Button } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { TableModule } from 'primeng/table';
import { HasHabilitationDirective } from '../../../../../shared/directives/has-habilitation/has-habilitation.directive';
import { FloatLabel } from 'primeng/floatlabel';
import { InputText } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { OperateursService } from '../../../../../data/intervenant/services/operateurs/operateurs.service';

@Component({
    selector: 'app-synthese-volumes',
    providers: [DecimalPipe],
    templateUrl: './synthese-volumes.component.html',
    styleUrls: ['./synthese-volumes.component.scss'],
    imports: [
        SaisirRevendicationComponent,
        SyntheseSaisieRevendicationComponent,
        ColumnVisibilityModalComponent,
        FaIconComponent,
        Button,
        TranslateModule,
        TooltipModule,
        NgClass,
        TableModule,
        ReactiveFormsModule,
        HasHabilitationDirective,
        FloatLabel,
        InputText,
        MessageModule
    ],
    standalone: true
})
export class SyntheseVolumesComponent implements OnInit {
    static routes: Route[] = [
        { path: '', component: SyntheseVolumesComponent, data: { menu: { libelle: 'Synthèse', icon: 'balance-scale' } } },
        { path: ':campagne', component: SyntheseVolumesComponent }
    ];


    private readonly document = inject(DOCUMENT);

    @Input() modalMode = false;
    @Input() readOnly = false;
    @Input() campagneInitiale?: string;
    @Input() idIntervenantInput?: number;
    @Input() raisonSociale?: string;

    public synthesesVolumesAOC: SyntheseVolumesAOC[];
    public synthesesVolumesIGP: SyntheseVolumesIGP[];
    public formGroup: UntypedFormGroup;
    public useAOC: boolean;
    public useIGP: boolean;

    protected readonly SessionContext = SessionContext;
    protected readonly UtilisateurTypeCode = UtilisateurTypeCode;

    /**
     * List of columns AOC
     */
    columnsAOC: Column[] = [
        { field: 'produit', label: 'page.declarations.synthese.table.header.produit', visible: true },
        { field: 'surface', label: 'page.declarations.synthese.table.header.surface', visible: true },
        { field: 'volumesRevendiques', label: 'page.declarations.synthese.table.header.volumesRevendiques', visible: true },
        { field: 'vciRevendiques', label: 'page.declarations.synthese.table.header.vciRevendiques', visible: true },
        { field: 'achatVrac', label: 'page.declarations.synthese.table.header.achatVrac', visible: true },
        { field: 'chgtProduitEntrees', label: 'page.declarations.synthese.table.header.chgtProduitEntrees', visible: false },
        { field: 'volumesTirages', label: 'page.declarations.synthese.table.header.volumesTirages', visible: false },
        { field: 'totalEntrees', label: 'page.declarations.synthese.table.header.totalEntrees', visible: true },
        { field: 'venteVrac', label: 'page.declarations.synthese.table.header.venteVrac', visible: false },
        { field: 'conditionnes', label: 'page.declarations.synthese.table.header.conditionnes', visible: false },
        { field: 'chgtProduitSorties', label: 'page.declarations.synthese.table.header.chgtProduitSorties', visible: false },
        { field: 'volumesTiragesBase', label: 'page.declarations.synthese.table.header.volumesTiragesBase', visible: false },
        { field: 'totalSorties', label: 'page.declarations.synthese.table.header.totalSorties', visible: true },
        { field: 'volumesStockVrac', label: 'page.declarations.synthese.table.header.volumesStockVrac', visible: true }
    ];

    /**
     * List of columns IGP
     */
    columnsIGP: Column[] = [
        { field: 'produit', label: 'page.declarations.synthese.table.header.produit' },
        { field: 'surface', label: 'page.declarations.synthese.table.header.surface' },
        { field: 'volIssusDr', label: 'page.declarations.synthese.table.header.volumesIssusDR' },
        { field: 'volRevendiques', label: 'page.declarations.synthese.table.header.volumesRevendiques' },
        { field: 'varRevendiqueChangementsDeno', label: 'page.declarations.synthese.table.header.variationChgtDeno' },
        { field: 'varRevendiqueDeclassement', label: 'page.declarations.synthese.table.header.variationDeclassement' },
        { field: 'resteARevendiquer', label: 'page.declarations.synthese.table.header.resteARevendiquer' },
        { field: 'volTransactions', label: 'page.declarations.synthese.table.header.volumesTransactions' }
    ];

    /**
     * Ref to html modal component
     */
    @ViewChild('columnModal', { static: true }) columnModal: any;

    /**
     * Local storage key of columns visible in synthese volume of operator
     */
    readonly STORAGE_KEY = 'synthses_volume_operator_columns_visibility';

    // ── Dialog saisie revendication ──
    saisieDialogVisible = false;
    saisieDialogLot: LotSaisiesRevendications | undefined;
    saisieDialogIndex: number | undefined;

    // ── Dialog synthèse ──
    syntheseDialogVisible = false;
    syntheseLot: LotSaisiesRevendications | undefined;
    creerRevendicationSynthese = false;

    get campagne() {
        return this.formGroup.get('campagne');
    }

    /**
     * Signals
     */
    showColumnVisibilityModal = signal(false);

    constructor(
        private readonly syntheseVolumesService: SyntheseVolumesService,
        private readonly fb: UntypedFormBuilder,
        private readonly modalService: NgbModal,
        private readonly translate: TranslateService,
        private readonly route: ActivatedRoute,
        private readonly router: Router,
        private readonly refCampagnesService: RefCampagnesService,
        private readonly produitsService: ProduitsService,
        private readonly cahiersService: CahiersService,
        private readonly loader: LoaderService,
        private readonly decimalPipe: DecimalPipe,
        private readonly operateursService: OperateursService,
        @Optional() public activeModal?: NgbActiveModal
    ) { }

    get idIntervenant(): number {

        if (this.idIntervenantInput) {
            return this.idIntervenantInput;
        }

        if (NavigationContext.has('idOperateur')) {
            return NavigationContext.get('idOperateur');
        } else if (this.operateursService.idOperateur()) {
            return this.operateursService.idOperateur();
        } else {
            return SessionContext.get('idIntervenant');
        }
    }

    ngOnInit() {
        this.formGroup = this.fb.group({
            campagne: [undefined, [Validators.required, Validators.pattern(/(19|20)\d\d/)]]
        });

        if (this.modalMode) {
            if (this.campagneInitiale) {
                this.campagne.setValue(this.campagneInitiale, { emitEvent: false });
                this.loadData(this.campagneInitiale);
            } else {
                this.refCampagnesService.getCampagneEnCours().subscribe(campagne => {
                    const annee = campagne.annee.toString();
                    this.campagne.setValue(annee, { emitEvent: false });
                    this.loadData(annee);
                });
            }
        } else {
            this.route.params.subscribe(vals => {
                const campagne = vals.campagne;
                if (campagne) {
                    this.campagne.setValue(campagne, { emitEvent: false });
                    this.loadData(campagne);
                } else {
                    this.refCampagnesService.getCampagneEnCours().subscribe(campagne => {
                        const annee = campagne.annee.toString();
                        this.campagne.setValue(annee, { emitEvent: false });
                        this.loadData(annee);
                    });
                }
            });
        }

        this.campagne.valueChanges.subscribe(campagne => {
            if (this.campagne.valid) {
                this.synthesesVolumesAOC = null;
                this.synthesesVolumesIGP = null;
                
                if (this.modalMode) {
                    this.loadData(campagne);
                } else {
                    this.router.navigate([campagne], { relativeTo: this.route.parent });
                }
            }
        });

        this.loadColumnVisibility();
    }

    loadData(campagne: string) {
        // Récupérer les synthèses AOC et IGP
        forkJoin([
            this.cahiersService.getUseTypeCahier(this.idIntervenant, TypeCahierCode.AOC),
            this.cahiersService.getUseTypeCahier(this.idIntervenant, TypeCahierCode.IGP),
            this.syntheseVolumesService.getSyntheseVolumesAOC(this.idIntervenant, campagne),
            this.syntheseVolumesService.getSyntheseVolumesIGP(this.idIntervenant, campagne)
        ])
            .pipe(this.loader.operator())
            .subscribe(([useAOC, useIGP, synthesesAOC, synthesesIGP]) => {
                this.useAOC = useAOC;
                this.useIGP = useIGP;
                this.synthesesVolumesAOC = [...synthesesAOC].sort((a, b) => a.produit?.libelle.localeCompare(b.produit?.libelle));
                this.synthesesVolumesIGP = [...synthesesIGP].sort((a, b) => a.produit?.libelle.localeCompare(b.produit?.libelle));
            });
    }

    // ###############################
    // ###########  AOC  #############
    // ###############################
    changementDenomination(synthese: SyntheseVolumesAOC) {
        const modal = this.modalService.open(ChangementsDenominationComponent, {
            backdrop: 'static',
            windowClass: 'app-modal-1000px'
        });
        modal.componentInstance.produitsOrigine = [synthese.produit];
        modal.componentInstance.campagne = this.campagne.value;
        modal.componentInstance.typeCahier = TypeCahierCode.AOC;
        modal.componentInstance.stockOrigine = StockCode.REVENDIQUE;

        modal.result.then(
            () => {
                this.loadData(this.campagne.value);
            },
            () => {}
        );
    }

    saisirConditionnement(synthese: SyntheseVolumesAOC) {
        const modal = this.modalService.open(SaisirConditionnementComponent, {
            backdrop: 'static',
            windowClass: 'app-modal-1000px'
        });
        modal.componentInstance.syntheseProduit = synthese.produit;
        modal.componentInstance.campagne = this.campagne.value;
        modal.componentInstance.estCreation = true;

        modal.result.then(
            () => {
                this.loadData(this.campagne.value);
            },
            () => {}
        );
    }

    // ###############################
    // ###########  IGP  #############
    // ###############################

    changementDenominationIgp(synthese: SyntheseVolumesIGP) {
        const modal = this.modalService.open(ChangementsDenominationComponent, {
            backdrop: 'static',
            windowClass: 'app-modal-1000px'
        });
        modal.componentInstance.produitsOrigine = [synthese.produit];
        modal.componentInstance.campagne = this.campagne.value;
        modal.componentInstance.typeCahier = TypeCahierCode.IGP;
        modal.componentInstance.stockOrigine = StockCode.REVENDIQUE;

        modal.result.then(
            () => {
                this.loadData(this.campagne.value);
            },
            () => {}
        );
    }

    SaisirRevendicationIgp(lotRevendications: LotSaisiesRevendications = undefined, indexAModifier: number = undefined) {
        this.saisieDialogLot = lotRevendications;
        this.saisieDialogIndex = indexAModifier;
        this.saisieDialogVisible = true;
    }

    onSaisieRevendicationTerminer(lot: LotSaisiesRevendications) {
        this.syntheseLot = lot;
        this.creerRevendicationSynthese = true;
        this.syntheseDialogVisible = true;
    }

    onSyntheseRevendicationClose(result: SaisieRevendicationResult | null) {
        if (result?.saisieTerminee) {
            this.loadData(this.campagne.value);
        } else if (result) {
            this.SaisirRevendicationIgp(result.lotRevendications, result.indexAModifier);
        }
    }

    saisirConditionnementIgp(synthese: SyntheseVolumesIGP) {
        const modal = this.modalService.open(SaisirConditionnementIgpComponent, {
            backdrop: 'static',
            windowClass: 'app-modal-1000px'
        });
        modal.componentInstance.syntheseProduit = synthese.produit;
        modal.componentInstance.campagne = this.campagne.value;

        modal.result.then(
            () => {
                this.loadData(this.campagne.value);
            },
            () => {}
        );
    }

    modifierDr(synthese: SyntheseVolumesIGP) {
        const modal = this.modalService.open(DonneesDrComponent, { backdrop: 'static', windowClass: 'app-modal-60' });
        modal.componentInstance.syntheseProduit = synthese.produit;
        modal.componentInstance.annee = this.campagne.value;
        modal.result.then(
            () => {
                this.loadData(this.campagne.value);
            },
            () => {}
        );
    }

    // ###############################
    // #########  AOC + IGP ##########
    // ###############################

    changementDenominationHorsBacchus(type: TypeCahierCode) {
        this.produitsService
            .getProduits(this.idIntervenant, false)
            .pipe(this.loader.operator())
            .subscribe(produits => {
                const modal = this.modalService.open(ChangementsDenominationComponent, {
                    backdrop: 'static',
                    windowClass: 'app-modal-1000px'
                });
                modal.componentInstance.produitsOrigine = produits;
                modal.componentInstance.campagne = this.campagne.value;
                modal.componentInstance.typeCahier = type;
                modal.componentInstance.stockOrigine = type === TypeCahierCode.IGP ? StockCode.ISSU_DR : StockCode.REVENDIQUE;
                modal.componentInstance.origineHorsBacchus = true;
                modal.componentInstance.estCreation = true;

                modal.result.then(
                    () => {
                        this.loadData(this.campagne.value);
                    },
                    () => {}
                );
            });
    }

    ajoutProduit(type: TypeCahierCode) {
        const modal = this.modalService.open(RechercheProduitComponent, { backdrop: 'static' });
        modal.componentInstance.typeCahierCode = type;
        modal.componentInstance.produitBacchus = true;
        modal.componentInstance.idOperateur = this.idIntervenant;

        switch (type) {
            case TypeCahierCode.AOC:
                modal.componentInstance.alreadyPresentIds = this.synthesesVolumesAOC.map(item => item.produit.id);
                break;
            case TypeCahierCode.IGP:
                modal.componentInstance.alreadyPresentIds = this.synthesesVolumesIGP.map(item => item.produit.id);
                break;
            default:
                Fail.never(type);
        }

        modal.result.then(
            (produit: Produit) => {
                switch (type) {
                    case TypeCahierCode.AOC:
                        this.synthesesVolumesAOC.push(Object.assign(new SyntheseVolumesAOC(), { produit, codeProduit: produit.code }));
                        break;
                    case TypeCahierCode.IGP:
                        this.synthesesVolumesIGP.push(Object.assign(new SyntheseVolumesIGP(), { produit, codeProduit: produit.code }));
                        break;
                    default:
                        Fail.never(type);
                }
            },
            () => {}
        );
    }

    transaction(typeCahier: TypeCahierCode, produit: Produit) {
        const modal = this.modalService.open(SaisirTransactionComponent, {
            backdrop: 'static',
            windowClass: 'app-modal-1000px'
        });
        modal.componentInstance.typeCahier = typeCahier;
        modal.componentInstance.produit = produit;
        modal.componentInstance.campagne = this.campagne.value;
        modal.componentInstance.estCreation = true;

        modal.result.then(
            () => {
                this.loadData(this.campagne.value);
            },
            () => {}
        );
    }

    tirage(typeCahier: TypeCahierCode, synthese: SyntheseVolumesAOC) {
        const modal = this.modalService.open(SaisirTirageComponent, {
            backdrop: 'static',
            windowClass: 'app-modal-1000px'
        });
        modal.componentInstance.typeCahier = typeCahier;
        modal.componentInstance.syntheseProduit = synthese.produit;
        modal.componentInstance.campagne = this.campagne.value;
        modal.componentInstance.estCreation = true;

        modal.result.then(
            () => {
                this.loadData(this.campagne.value);
            },
            () => {}
        );
    }

    /**
     * Toggle column visibility
     * @param field - Column
     */
    toggleColumnVisibility(field: string): void {
        const col = this.columnsAOC.find(c => c.field === field);
        if (col) {
            col.visible = !col.visible;
            this.saveColumnVisibility();
        }
    }

    /**
     * Save columns visible
     */
    private saveColumnVisibility(): void {
        const visibility = this.columnsAOC.map(col => ({ field: col.field, visible: col.visible }));
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(visibility));
    }

    /**
     * True if column visible
     * @param field - Column
     */
    isColumnVisible(field: string): boolean {
        const column = this.columnsAOC.find(c => c.field === field);
        return column ? column.visible : false;
    }

    /**
     * Open column modal
     */
    openColumnModal() {
        this.document.body.classList.add('column-visibility-open');
        this.showColumnVisibilityModal.set(true);

        // PrimeNG crée et positionne le masque après l'ouverture.
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                const masks = Array.from(
                    this.document.body.querySelectorAll<HTMLElement>('.p-dialog-mask')
                );

                const lastMask = masks[masks.length - 1];

                if (!lastMask) {
                    return;
                }

                lastMask.style.setProperty('z-index', '20000', 'important');

                lastMask
                    .querySelector<HTMLElement>('.p-dialog')
                    ?.style.setProperty('z-index', '20001', 'important');
            });
        });
    }

    onColumnModalVisibleChange(visible: boolean): void {
        this.showColumnVisibilityModal.set(visible);

        if (!visible) {
            this.document.body.classList.remove('column-visibility-open');
        }
    }

    onColumnsChange(columns: Column[]): void {
        if (this.useIGP && !this.useAOC) {
            this.columnsIGP = columns;
        } else {
            this.columnsAOC = columns;
        }
    }

    /**
     * Get nb of columns visible
     */
    getVisibleColumnCount(): number {
        return this.columnsAOC.filter(col => col.visible).length;
    }

    /**
     * Load columns visible
     */
    loadColumnVisibility(): void {
        const saved = localStorage.getItem(this.STORAGE_KEY);
        if (saved) {
            const parsed = JSON.parse(saved);
            this.columnsAOC.forEach(col => {
                const savedCol = parsed.find((c: any) => c.field === col.field);
                if (savedCol) {
                    col.visible = savedCol.visible;
                }
            });
        }
    }

    getCellValueIGP(field: string, synthese: any): string {
        switch (field) {
            case 'produit':
                return synthese?.produit?.libelle ?? '';
            case 'surface':
                return this.decimalPipe.transform(synthese.surface, '1.4-4');
            case 'volIssusDr':
                return this.decimalPipe.transform(synthese.volIssusDr, '1.2-2');
            case 'volRevendiques':
                return this.decimalPipe.transform(synthese.volRevendiques, '1.2-2');
            case 'varRevendiqueChangementsDeno':
                return this.decimalPipe.transform(synthese.varRevendiqueChangementsDeno, '1.2-2');
            case 'varRevendiqueDeclassement':
                return this.decimalPipe.transform(synthese.varRevendiqueDeclassement, '1.2-2');
            case 'resteARevendiquer':
                return this.decimalPipe.transform(synthese.resteARevendiquer, '1.2-2');
            case 'volTransactions':
                return this.decimalPipe.transform(synthese.volTransactions, '1.2-2');
            default:
                return '';
        }
    }

    /**
     * Get cellule value
     * @param field - Column
     * @param synthese - Data
     */
    getCellValue(field: string, synthese: any): string {
        const totalEntrees =
            Math.abs(synthese.volumesRevendiques) +
            Math.abs(synthese.volumesAchats) +
            Math.abs(synthese.chgtProduitEntrees) +
            Math.abs(synthese.volumesTirages) +
            Math.abs(synthese.vciRevendiques);

        const totalSorties =
            Math.abs(synthese.volumesConditionnes) +
            Math.abs(synthese.volumesVentes) +
            Math.abs(synthese.chgtProduitSorties) +
            Math.abs(synthese.volumesTiragesVinDeBase);

        switch (field) {
            case 'produit':
                if (synthese && synthese.produit && synthese.produit.libelle) {
                    return synthese.produit.libelle;
                }
                return '';
            case 'surface':
                return this.decimalPipe.transform(synthese.surface, '1.4-4');
            case 'volumesRevendiques':
                return this.decimalPipe.transform(Math.abs(synthese.volumesRevendiques), '1.2-2');
            case 'vciRevendiques':
                return this.decimalPipe.transform(Math.abs(synthese.vciRevendiques), '1.2-2');
            case 'achatVrac':
                return this.decimalPipe.transform(Math.abs(synthese.volumesAchats), '1.2-2');
            case 'venteVrac':
                return this.decimalPipe.transform(Math.abs(synthese.volumesVentes), '1.2-2');
            case 'chgtProduitEntrees':
                return this.decimalPipe.transform(Math.abs(synthese.chgtProduitEntrees), '1.2-2');
            case 'chgtProduitSorties':
                return this.decimalPipe.transform(Math.abs(synthese.chgtProduitSorties), '1.2-2');
            case 'volumesTirages':
                return this.decimalPipe.transform(Math.abs(synthese.volumesTirages), '1.2-2');
            case 'volumesTiragesBase':
                return this.decimalPipe.transform(Math.abs(synthese.volumesTiragesVinDeBase), '1.2-2');
            case 'totalEntrees':
                return this.decimalPipe.transform(totalEntrees, '1.2-2');
            case 'totalSorties':
                return this.decimalPipe.transform(totalSorties, '1.2-2');
            case 'conditionnes':
                return this.decimalPipe.transform(Math.abs(synthese.volumesConditionnes), '1.2-2');
            case 'volumesStockVrac':
                return this.decimalPipe.transform(totalEntrees - totalSorties, '1.2-2');
            default:
                return '';
        }
    }

    protected readonly TypeCahierCode = TypeCahierCode;
}
